import { useCallback, useEffect, useRef, useState } from 'react';
import { useClient } from '@web/api';
import {
  LivePricesDocument,
  LivePricesQuery,
  LivePricesQueryVariables,
} from '@/api/livePrices.generated';
import { useAppSelector } from '@/store/hooks';
import { ErpType, LivePrice } from '@/types';

interface InventoryItem {
  inventoryId: string;
  erpArticleId: string;
}

const BATCH_SIZE = 3;
const BATCH_DELAY_MS = 100;
const FETCH_DEBOUNCE_MS = 150;

const useLivePrices = ({
  inventories,
  customerId,
  retailerId,
  erpCustomerGroupID,
}: {
  inventories: InventoryItem[];
  customerId?: string;
  retailerId: string;
  erpCustomerGroupID?: string;
}): {
  livePrices: LivePrice[];
  fetching: boolean;
  error?: Error;
} => {
  const client = useClient();
  const [livePrices, setLivePrices] = useState<LivePrice[]>([]);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const settings = useAppSelector((state) => state.retailer.retailer?.settings);
  const livePriceErpType = settings?.livePriceErpType;
  const shouldFetch = livePriceErpType === ErpType.Serp;

  // A "fetch session" id to avoid race conditions
  const fetchSessionRef = useRef(0);
  const debounceTimeoutRef = useRef<any | null>(null);

  const fetchBatch = useCallback(
    async (batch: InventoryItem[], sessionId: number) => {
      try {
        console.log('Fetching batch with variables:', {
          erpCustomerId: customerId,
          erpType: livePriceErpType,
          retailerId,
          inventories: batch,
          erpCustomerGroupID,
        });

        const result = await client
          .query<LivePricesQuery, LivePricesQueryVariables>(
            LivePricesDocument,
            {
              in: {
                erpCustomerId: customerId!,
                erpType: livePriceErpType!,
                retailerId,
                inventories: batch,
                erpCustomerGroupId: erpCustomerGroupID,
              },
            },
          )
          .toPromise();

        // Prevent stale results from overwriting
        if (sessionId !== fetchSessionRef.current) {
          console.log('Stale batch result ignored');
          return [];
        }

        if (result.error) {
          throw result.error;
        }

        const prices = result.data?.livePrices || [];
        console.log('Batch prices received:', prices.length);
        return prices;
      } catch (err) {
        console.error('Error fetching live prices batch:', err);
        throw err;
      }
    },
    [client, customerId, retailerId, livePriceErpType, erpCustomerGroupID],
  );

  const fetchAllBatches = useCallback(
    async (sessionId: number) => {
      if (
        !erpCustomerGroupID ||
        !customerId ||
        inventories.length === 0 ||
        !shouldFetch
      ) {
        console.log('Skipping fetch - no customerId or inventories');
        if (sessionId === fetchSessionRef.current) {
          setLivePrices([]);
          setFetching(false);
        }
        return;
      }

      console.log('Starting fetchAllBatches:', {
        customerId,
        inventoriesLength: inventories.length,
      });

      setFetching(true);
      setError(undefined);

      try {
        const allPrices: LivePrice[] = [];

        for (let i = 0; i < inventories.length; i += BATCH_SIZE) {
          if (sessionId !== fetchSessionRef.current) {
            console.log('Fetch aborted mid-batch');
            return;
          }

          const batch = inventories.slice(i, i + BATCH_SIZE);
          console.log(
            `Fetching batch ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(
              inventories.length / BATCH_SIZE,
            )} with ${batch.length} items`,
          );

          const batchPrices = await fetchBatch(batch, sessionId);
          if (sessionId !== fetchSessionRef.current) return;

          allPrices.push(
            ...batchPrices.filter(
              (p): p is NonNullable<typeof p> => p! !== null,
            ),
          );

          if (sessionId === fetchSessionRef.current) {
            setLivePrices([...allPrices]);
          }

          if (i + BATCH_SIZE < inventories.length) {
            await new Promise((r) => setTimeout(r, BATCH_DELAY_MS));
          }
        }

        if (sessionId === fetchSessionRef.current) {
          console.log(`Completed fetching ${allPrices.length} live prices`);
        }
      } catch (err) {
        if (sessionId === fetchSessionRef.current) {
          setError(err instanceof Error ? err : new Error('Unknown error'));
        }
      } finally {
        if (sessionId === fetchSessionRef.current) {
          setFetching(false);
        }
      }
    },
    [erpCustomerGroupID, customerId, inventories, shouldFetch, fetchBatch],
  );

  useEffect(() => {
    if (!shouldFetch) {
      console.log('Skipping fetch - shouldFetch is false');
      setLivePrices([]);
      setError(undefined);
      setFetching(false);
      return;
    }
    setFetching(true);

    // Debounce to avoid immediate re-fetch on rapid changes
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      const newSessionId = Date.now();
      fetchSessionRef.current = newSessionId;
      fetchAllBatches(newSessionId);
    }, FETCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      // Increment session id to invalidate in-flight fetches
      fetchSessionRef.current = -1;
    };
  }, [
    shouldFetch,
    customerId,
    retailerId,
    inventories,
    erpCustomerGroupID,
    fetchAllBatches,
  ]);

  return { livePrices, fetching, error };
};

export default useLivePrices;
